/**
 * Basic view for springyModel linked objects.
 * Dependencies: jQuery
 *
 * Would like to do this without jQuery but the event delegation gets real complicated
 * and I don't have time to get it all worked out now.
 */

import {
    findFirstTabFocusable,
    findLastTabFocusable,
    deepActiveElement,
    findFirstFocusable
} from './focusUtil.js';

/**
 * Basic object for Views.
 */
const springyView = {
    /**
     * @prop {Object} el DOM element for the view container
     */
    el: null,
    /**
     * @prop {Object} $el jQuery object for this.el
     */
    $el: null,
    /**
     * VIEW MUST HAVE UNIQUE IDS!!!
     * @prop {String} id HTML id of the view container
     */
    id: '',
    /**
     * if you aren't passing in an element (this.el) then this will be used to create an element.
     * @prop {String} tagName HTML Tag for this view
     */
    tagName: 'div',
    /**
     * if you aren't passing in an element (this.el) then this will be used to create an element
     * @prop {String} className Classes for this view
     */
    className: '',
    /**
     * @prop {Object} model Model for this view.
     */
    model: null,
    /**
     * DOM events for elements inside the view
     * @prop {Object} events Events for delegation, see docs.
     */
    events: {},
    /**
     * Listeners on the model.
     * @prop {Object} listeners prop name is event, value is callback name (or multiple ones, space separated)
     */
    listeners: {},
    /**
     * Template engine object (could be anything that features a render method, I guess?)
     * Favoring nunjucks templates right now.
     * @prop {Object} templateEng
     */
    templateEng: null,
    /**
     * @prop {Object} templates Compiled templates for use in view.
     */
    templates: {
        main: function() { return ''; }
    },

    /**
     * Initialize the view to a model and create a DOM element.
     * You must pass in either el (a DOM element) or $el (a jQuery Element) or id (so one can be created).
     * @param {Object} config Properties to set like: model, el, events, id, tagName, className,.
     */
    init: function(config) {
        if (typeof config === 'undefined') {
            config = {};
        }
        const props = Object.keys(config);
        props.forEach(function(prop) {
            if (prop === 'events' || prop === 'listeners') {
                const keys = Object.keys(config[prop]);
                keys.forEach(function(key) {
                    this[prop][key] = config[prop][key];
                }, this);
                return;
            }
            this[prop] = config[prop];
        }, this);

        this.initElement();

        this.registerEvents(this.events);
        this.registerListeners(this.listeners);
    },
    /**
     * Create a DOM element based on certain properties.
     */
    generateElement: function() {
        if (!this.id) {
            throw Error('springyView el must have an id.');
        }
        this.el = document.createElement(this.tagName);
        this.el.id = this.id;
        this.$el = window.$(this.el);
        if (this.className !== '') {
            this.$el.addClass(this.className);
        }
    },
    /**
     * Confirm we have necessary properties set to find/generate an element
     */
    checkElementConfig: function() {
        if (this.el === null && this.$el === null && !this.id) {
            throw Error('springyView el, $el, or id must be set.');
        }
        if (this.el === null && this.$el === null) {
            const idEl = window.$(`#${this.id}`);
            if (idEl.length === 0) {
                this.generateElement();
                return;
            }
            this.$el = idEl;
        }
    },
    /**
     * Set some props (or add some) when an el/$el/id is passed in to init
     */
    initElement: function() {
        this.checkElementConfig();

        if (this.el === null) {
            this.el = this.$el[0];
        } else if (this.$el === null) {
            this.$el = window.$(this.el);
        }
        this.id = this.el.id;
        if (!this.id) {
            throw Error('springyView el must have an id.');
        }
        this.tagName = this.el.tagName;
        this.className = this.$el.attr('class');
    },
    /**
     * Add delegated events to this.$el
     * Note: events that are not view methods will get called with "this" bound to the dom element
     * Unless you send in bound object (using Function.prototype.bind)
     * @param {Object} events Object of events.
     */
    registerEvents: function(events) {
        const event_names = Object.keys(events);
        event_names.forEach(function(event) {
            this.addEvent(event, events[event]);
        }, this);
    },
    /**
     * Add event listener.
     * @param {String} event The event plus the delegation selector.
     * @param {Function|String} callback Function or method to call.
     */
    addEvent: function(event, callback) {
        if (!this.events[event]) {
            this.events[event] = callback;
        }
        // callback is either a function or string referencing a local function property.
        if (typeof callback !== 'function') {
            callback = this[callback].bind(this);
        }
        const parts = event.match(/^(\S+)\s*(.*)$/i);
        const real_event = parts[1];
        const selector = parts[2];
        this.$el.on(`${real_event}.viewDelegated-${this.id}`, selector, callback);
    },
    /**
     * Add listeners to the model emitter
     * Note: all listeners bound to view
     * @param {Object} listeners Object of listeners.
     */
    registerListeners: function(listeners) {
        const event_names = Object.keys(listeners);
        event_names.forEach(function(event) {
            this.listen(event, listeners[event]);
        }, this);
    },
    /**
     * Listen for events emitted from the model.
     * Callback will by called bound to the current view.
     * Callback arg can be multiple methods, space separated.
     * obj must have an emitter property
     * @param {String} event Event to listen for
     * @param {String} callback Method on this view (automatically bound to the view)
     */
    listen: function(event, callback) {
        if (this.model && this.model.emitter) {
            this.listeners[event] = callback;
            // split on space and then register multiple callbacks for the same event.
            const callbacks = callback.split(' ');
            callbacks.forEach(function(cb) {
                this.model.emitter.on(event, this[cb], this);
            }, this);
        }
    },
    /**
     * Remove listeners
     */
    stopListening: function() {
        if (!this.model || !this.model.emitter) {
            this.listeners = {};
            return;
        }
        const events = Object.keys(this.listeners);
        events.forEach(function(event) {
            const callback = this.listeners[event];
            const callbacks = callback.split(' ');
            callbacks.forEach(function(cb) {
                this.model.emitter.off(event, this[cb]);
            }, this);
        }, this);
        this.listeners = {};
    },
    /**
     * For trapping focus in the view assign as a handle to a keydown event on the view.
     * adding .breakfocus will cancel this out
     * @param {KeyboardEvent} ev Event
     */
    trapFocus: function(ev) {
        if (ev.key !== 'Tab' || this.el.classList.contains('breakfocus')) {
            return;
        }
        // Get current active/focused element.
        const currentEl = deepActiveElement();
        if (!currentEl) {
            return;
        }
        if (ev.shiftKey) {
            // Shift tab, only concerned with first element.
            if (currentEl === findFirstFocusable(this.el)
                || currentEl === findFirstTabFocusable(this.el)) {
                // Go to the last element in the modal.
                const focusable = findLastTabFocusable(this.el);
                if (focusable) {
                    ev.preventDefault();
                    focusable.focus();
                }
            }
            return;
        }
        // Tab (no shift), only concerned with last element.
        if (currentEl === findLastTabFocusable(this.el)) {
            // Go to the first element in the modal.
            const focusable = findFirstTabFocusable(this.el);
            if (focusable) {
                ev.preventDefault();
                focusable.focus();
            }
        }
    },
    /**
     * Remove the view element and stop listeners and events
     */
    remove: function() {
        this.stopListening();
        this.$el.remove(); // this should remove all events anyway.
    },
    /**
     * Render the view. Return the view.
     * Override this in any linked objects.
     * @return {Object}
     */
    render: function() {
        return this;
    }
};

export default springyView;
